/**
 * Adresses géolocalisées de la prise d'appel (migration 0030) — logique PURE, testée.
 *
 * Une adresse = un POINT (latitude/longitude) obtenu de trois façons — épingle posée sur la carte,
 * Plus Code, adresse déjà utilisée — plus un texte et un repère (« face à la pharmacie, portail
 * bleu ») que lira le livreur. Le quartier de la liste ne sert plus qu'à recentrer la carte.
 */
import { coordonneeValide } from './geo'

/* Centres APPROXIMATIFS des quartiers proposés, pour recentrer la carte. Ils ne servent JAMAIS de
   point de livraison : le point vient de l'épingle, du Plus Code ou d'une adresse connue. */
export const CENTRES_QUARTIERS: Readonly<Record<string, readonly [number, number]>> = {
  'Hamdallaye ACI 2000': [12.636, -8.023],
  Badalabougou: [12.6234, -7.9906],
  Hippodrome: [12.6495, -7.9925],
  'Kalaban Coura': [12.5844, -7.9577],
  'Djicoroni Para': [12.6192, -8.0372],
  Magnambougou: [12.6232, -7.9578],
  Sogoniko: [12.593, -7.976],
  Lafiabougou: [12.6585, -8.019],
  'Quartier du Fleuve': [12.6471, -7.9961],
  Niaréla: [12.6416, -7.9887],
}

/** Comment le point a été obtenu — vocabulaire de `adresses.source` (0030) côté console. */
export type SourcePoint = 'epingle' | 'plus_code'

/** Ce que saisit le formulaire pour UNE extrémité du trajet. */
export interface AdresseSaisie {
  /** Adresse en clair (« Marché de Lafiabougou, rue 12 »). */
  texte: string
  /** Repère à l'attention du livreur. */
  repere: string
  lat: number | null
  lng: number | null
  /** D'où vient le point (pour la mémorisation) ; null = point repris d'une adresse connue ou absent. */
  source: SourcePoint | null
}

export const ADRESSE_VIDE: AdresseSaisie = { texte: '', repere: '', lat: null, lng: null, source: null }

export function aUnPoint(a: Pick<AdresseSaisie, 'lat' | 'lng'>): a is { lat: number; lng: number } & typeof a {
  return coordonneeValide(a.lat, a.lng)
}

/**
 * Libellé écrit dans `courses.pickup_address` / `dropoff_address` — ce que le livreur lit.
 * Texte, puis « — repère » ; sans texte, le repère seul ; sans rien mais avec un point, une mention
 * explicite (jamais une adresse vide : le livreur doit savoir qu'il suit une épingle).
 */
export function libelleAdresse(a: AdresseSaisie, plusCode?: string | null): string {
  const texte = a.texte.trim()
  const repere = a.repere.trim()
  if (texte && repere) return `${texte} — ${repere}`
  if (texte) return texte
  if (repere) return repere
  if (aUnPoint(a)) return plusCode ? `Point sur la carte (Plus Code ${plusCode})` : 'Point sur la carte'
  return ''
}

/** Une ligne de `adresses` (0030), telle que la console la lit. */
export interface AdresseConnue {
  id: string
  label: string
  address_text: string | null
  repere: string | null
  lat: number
  lng: number
  source: 'epingle' | 'plus_code' | 'lien_destinataire' | 'livraison' | 'import'
  use_count: number
  last_used_at: string
  contact_phone: string | null
}

export const LIBELLE_SOURCE: Record<AdresseConnue['source'], string> = {
  epingle: 'épingle',
  plus_code: 'Plus Code',
  lien_destinataire: 'position envoyée par le destinataire',
  livraison: 'point de livraison',
  import: 'import',
}

/** Intitulé court d'une adresse connue pour un bouton. */
export function intituleAdresse(a: AdresseConnue): string {
  const base = a.label.trim() || a.address_text?.trim() || a.repere?.trim() || 'Adresse enregistrée'
  return base.length > 38 ? `${base.slice(0, 37)}…` : base
}

/** Ce que devient une adresse connue une fois choisie dans le formulaire. */
export function saisieDepuisConnue(a: AdresseConnue): AdresseSaisie {
  return { texte: a.address_text ?? a.label ?? '', repere: a.repere ?? '', lat: a.lat, lng: a.lng, source: null }
}

/**
 * Clé de comparaison d'un téléphone — JUMEAU de `public.cle_telephone()` (0030), mêmes vecteurs :
 * chiffres seuls, « 00 » initial retiré, indicatif malien 223 retiré quand il reste 8 chiffres,
 * `null` sous 8 chiffres.
 */
export function cleTelephone(tel: string | null | undefined): string | null {
  if (!tel) return null
  let d = tel.replace(/[^0-9]/g, '')
  if (d.startsWith('00')) d = d.slice(2)
  if (d.length === 11 && d.startsWith('223')) d = d.slice(3)
  return d.length < 8 ? null : d
}
